import { useState, useEffect, useCallback } from 'react';
import {
    listLinksPuzzles, approveLinksPuzzle, unscheduleLinksPuzzle, deleteLinksPuzzle, generateLinksPuzzle,
    setLinksPuzzleNumber,
} from '../../api/links';
import { isGenerating } from './linksPuzzles';

const POLL_MS = 4000;

// The Links page's data: every puzzle, the scheduled buffer and the latest
// generation per date, and the actions that change them. Each action
// reloads, since approving one puzzle moves the buffer and the suggested
// date; while a generation runs the list is polled until it settles.
const EMPTY = { items: [], buffer: 0, buffer_target: 14, generations: [] };

export default function useLinksPuzzles(token) {
    const [data, setData] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const reload = useCallback(async () => {
        // Defaults cover fields an older API build doesn't send yet.
        setData({ ...EMPTY, ...(await listLinksPuzzles(token)) });
    }, [token]);

    useEffect(() => {
        (async () => {
            try {
                await reload();
            } catch (err) {
                setError(err.message || 'Failed to load puzzles.');
            } finally {
                setLoading(false);
            }
        })();
    }, [reload]);

    const generating = isGenerating(data.generations);
    useEffect(() => {
        if (!generating) return undefined;
        const timer = setInterval(() => reload().catch(() => {}), POLL_MS);
        return () => clearInterval(timer);
    }, [generating, reload]);

    // Resolves true when the action succeeded.
    const run = useCallback(async (action) => {
        setError('');
        setBusy(true);
        try {
            await action();
            await reload();
            return true;
        } catch (err) {
            setError(err.message || 'Something went wrong.');
            return false;
        } finally {
            setBusy(false);
        }
    }, [reload]);

    const approve = (puzzle, date) => run(() => approveLinksPuzzle(token, puzzle.id, date));
    const generate = (request) => run(() => generateLinksPuzzle(token, request));
    const unschedule = (puzzle) => run(() => unscheduleLinksPuzzle(token, puzzle.id));
    const setNumber = (puzzle, number) => run(() => setLinksPuzzleNumber(token, puzzle.id, number));
    const remove = (puzzle) => {
        if (!window.confirm(`Delete puzzle #${puzzle.id}?`)) return;
        run(() => deleteLinksPuzzle(token, puzzle.id));
    };

    return { ...data, loading, busy, error, reload, approve, generate, unschedule, setNumber, remove };
}
